import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import Swal from "sweetalert2";
import { Apis } from "../../../lib/Apis";
import Loader from "../../../Utils/loader";

function AdminUserForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const userState = location.state?.user;
  const mode = location.state?.mode || (id ? "edit" : "create");
  const isViewMode = mode === "view";

  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: userState?.name || "",
    email: userState?.email || "",
  });
  const [selectedRoleIds, setSelectedRoleIds] = useState(
    new Set((userState?.roles || []).map((role) => role.id)),
  );

  const title = useMemo(() => {
    if (isViewMode) return "View Admin User";
    return id ? "Edit Admin User" : "Add Admin User";
  }, [id, isViewMode]);

  const goBack = () => navigate("/admin-users");

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        const rolesResponse = await Apis.getRoles(0, 1000, { status: true });
        setRoles(rolesResponse.data.data.roles || []);

        if (id && !userState) {
          const userResponse = await Apis.getAdminUserById(id);
          const user = userResponse.data.data;
          setFormData({
            name: user.name || "",
            email: user.email || "",
          });
          setSelectedRoleIds(
            new Set((user.roles || []).map((role) => role.id)),
          );
        }
      } catch (error) {
        Swal.fire(
          "Error",
          error.response?.data?.message || "Failed to load form data.",
          "error",
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [id, userState]);

  const toggleRole = (roleId) => {
    if (isViewMode) return;
    setSelectedRoleIds((prevSelected) => {
      const nextSelected = new Set(prevSelected);
      if (nextSelected.has(roleId)) {
        nextSelected.delete(roleId);
      } else {
        nextSelected.add(roleId);
      }
      return nextSelected;
    });
    if (errors.roles) {
      setErrors((prev) => ({ ...prev, roles: "" }));
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isViewMode) return;

    const nextErrors = {};
    if (!formData.name.trim()) {
      nextErrors.name = "Name is required.";
    }
    if (!formData.email.trim()) {
      nextErrors.email = "Email is required.";
    }
    if (selectedRoleIds.size === 0) {
      nextErrors.roles = "Please select at least one role.";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const payload = {
      name: formData.name.trim(),
      roleIds: [...selectedRoleIds],
    };

    if (!id) {
      payload.email = formData.email.trim();
    }

    try {
      setIsSubmitting(true);
      if (id) {
        await Apis.updateAdminUser(id, payload);
        Swal.fire("Success", "Admin user updated successfully.", "success");
      } else {
        await Apis.createAdminUser(payload);
        Swal.fire(
          "Success",
          "Admin user created and emailed successfully.",
          "success",
        );
      }
      goBack();
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to save admin user.",
        "error",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <Loader message="Loading admin user form..." />;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 pb-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="ui-btn-back"
            onClick={goBack}
            disabled={isSubmitting}
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h2 className="text-xl font-extrabold text-[#15173d]">{title}</h2>
            <p className="text-xs text-gray-500">Roles and Permissions</p>
          </div>
        </div>
        {!isViewMode && (
          <div className="flex gap-2">
            <button
              type="button"
              className="ui-btn-cancel"
              onClick={goBack}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="ui-btn-save"
              disabled={isSubmitting}
            >
              <Save size={15} />
              {isSubmitting ? "Saving..." : id ? "Update" : "Save"}
            </button>
          </div>
        )}
      </div>

      <div className="ui-card grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <label className="ui-form-label">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="name"
            className={`ui-input-field ${
              errors.name ? "border-brand-error" : ""
            }`}
            placeholder="Enter user name"
            value={formData.name}
            disabled={isViewMode}
            onChange={handleChange}
          />
          {errors.name ? <p className="ui-form-error">{errors.name}</p> : null}
        </div>
        <div>
          <label className="ui-form-label">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            name="email"
            className={`ui-input-field ${
              errors.email ? "border-brand-error" : ""
            }`}
            placeholder="Enter user email"
            value={formData.email}
            disabled={isViewMode || Boolean(id)}
            onChange={handleChange}
          />
          {errors.email ? (
            <p className="ui-form-error">{errors.email}</p>
          ) : id && !isViewMode ? (
            <p className="mt-1 text-xs text-gray-500">
              Email cannot be changed after user creation.
            </p>
          ) : null}
        </div>
      </div>

      <div className="ui-card">
        <label className="ui-form-label">
          Assign Roles <span className="text-red-500">*</span>
        </label>
        {roles.length === 0 ? (
          <p className="text-sm text-gray-500">No active roles found.</p>
        ) : (
          <div className="mt-1 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {roles.map((role) => {
              const checked = selectedRoleIds.has(role.id);
              return (
                <label
                  key={role.id}
                  className={`flex items-start gap-3 rounded-xl border p-3 transition-colors ${
                    checked
                      ? "border-[#982598]/40 bg-[#982598]/5"
                      : "border-[#15173d]/10 hover:bg-gray-50"
                  } ${isViewMode ? "cursor-default" : "cursor-pointer"}`}
                >
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 accent-[#982598]"
                    checked={checked}
                    disabled={isViewMode}
                    onChange={() => toggleRole(role.id)}
                  />
                  <span>
                    <span className="block font-medium text-[#15173d]">
                      {role.name}
                    </span>
                    <span className="mt-0.5 block text-sm text-gray-500">
                      {role.description || "No description"}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        )}
        {errors.roles ? (
          <p className="ui-form-error mt-2">{errors.roles}</p>
        ) : null}
      </div>
    </form>
  );
}

export default AdminUserForm;
